/**
 * PageActions - Portal component for page-level toolbar actions
 *
 * Enforces the platform toolbar ordering rule:
 *   (Page Actions)(Refresh)(Locking Action)
 *
 * Pages use this component to inject their action buttons into the
 * layout-level sticky toolbar. The EmergencyLockButton is always
 * rendered by the layout AFTER the portal target, ensuring it's
 * always the rightmost element.
 *
 * Usage:
 * ```tsx
 * <PageActions onRefresh={handleRefresh} refreshing={isRefreshing}>
 *   <Button variant="outline" onClick={handleRestart}>
 *     <RotateCw className="w-4 h-4 mr-2" /> Restart
 *   </Button>
 *   <Button onClick={handleStart}>
 *     <Play className="w-4 h-4 mr-2" /> Start
 *   </Button>
 * </PageActions>
 * ```
 *
 * The toolbar renders as:
 *   [Restart] [Start] | [Refresh] | [Lock Button]
 *
 * If a page has no actions, only the lock button shows in the toolbar.
 */

import { useState, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { RefreshCw } from 'lucide-react';

/** DOM ID for the portal target rendered by EMSLayout */
export const PAGE_ACTIONS_PORTAL_ID = 'ems-page-actions-portal';

interface PageActionsProps {
  /** Page-specific action buttons (rendered first) */
  children?: ReactNode;
  /** Refresh handler — renders a refresh button after page actions */
  onRefresh?: () => void;
  /** Whether a refresh is in progress (spins the icon, disables button) */
  refreshing?: boolean;
}

export function PageActions({ children, onRefresh, refreshing }: PageActionsProps) {
  const [container, setContainer] = useState<Element | null>(null);

  useEffect(() => {
    const el = document.getElementById(PAGE_ACTIONS_PORTAL_ID);
    setContainer(el);
  }, []);

  if (!container) return null;

  const hasContent = children || onRefresh;
  if (!hasContent) return null;

  return createPortal(
    <>
      {/* Page-specific action buttons */}
      {children}

      {/* Separator between page actions and refresh */}
      {children && onRefresh && (
        <div className="w-px h-6 bg-[var(--grok-border)]" />
      )}

      {/* Refresh button — always after page actions, before lock */}
      {onRefresh && (
        <button
          onClick={onRefresh}
          className="p-2 rounded-lg text-[var(--grok-text-secondary)] hover:text-[var(--grok-text-primary)] hover:bg-[var(--grok-surface-3)] transition-colors disabled:opacity-50"
          title="Refresh"
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      )}

      {/* Separator before the lock button (rendered by layout) */}
      <div className="w-px h-6 bg-[var(--grok-border)]" />
    </>,
    container,
  );
}
